// ============================================================================
// FSA NOTE — SHARED ROUNDING HELPERS
// ============================================================================
// Mirrors EngagementNoteDataService.AutoRound on the backend so frontend
// can preview rounded values immediately, before next save.
//
// Divisor per factor key:
//   THOUSANDS → 1,000        e.g. 5,000,000 → 5,000.00
//   LAKHS     → 100,000      e.g. 5,000,000 → 50.00
//   CRORES    → 10,000,000   e.g. 50,000,000 → 5.00
//   MILLIONS  → 1,000,000    e.g. 5,000,000 → 5.00
//   BILLIONS  → 1,000,000,000
//   ACTUALS / unknown → 1 (no divisor)
//
// All rounding is half-up to 2 decimal places (matches backend).

export type RoundingFactor =
  | 'ACTUALS'
  | 'THOUSANDS'
  | 'LAKHS'
  | 'CRORES'
  | 'MILLIONS'
  | 'BILLIONS'
  | string;

/** Default tolerance applied to disclosure amount cells when an auditor
 *  override drifts from the auto-rounded value. Mirrors
 *  fsa.tbl_fsa_engagement_settings.rounding_tolerance default. */
export const DEFAULT_ROUNDING_TOLERANCE = 1.0;

// Rounding-factor divisors are DYNAMIC — sourced from the rounding-factor
// enum's meta_data.divisor and delivered on the note hydrate
// (hydrate.roundingFactorDivisor), then registered via registerRoundingDivisor().
// Adding a new factor (e.g. HUNDREDS → 100) is a pure enum/data change — no edit
// needed here. The seeded values are only a safety fallback for the known legacy
// factors; the engagement's actual divisor always overrides them once registered.
const ROUNDING_DIVISORS: Record<string, number> = {
  THOUSANDS: 1_000,
  LAKHS: 100_000,
  CRORES: 10_000_000,
  MILLIONS: 1_000_000,
  BILLIONS: 1_000_000_000,
};

/** Register an engagement's rounding factor → divisor (from the hydrate's enum-resolved value). */
export function registerRoundingDivisor(
  factor: string | null | undefined,
  divisor: number | null | undefined,
): void {
  if (factor && typeof divisor === 'number' && divisor > 0) {
    ROUNDING_DIVISORS[factor] = divisor;
  }
}

export function autoRound(actual: number, factor: RoundingFactor | null | undefined): number {
  const divisor = (factor && ROUNDING_DIVISORS[factor]) || 1;
  return Math.round((actual / divisor) * 100) / 100;
}

/**
 * The rounded value to keep when an actual is refreshed (e.g. Update from
 * Ledger, run automatically on Save when the note drifts): the existing
 * rounded value — which may be the auditor's adjustment — while the actual is
 * unchanged, else the fresh auto-rounded value.
 */
export function keepRounded(
  oldActual: number | null | undefined,
  newActual: number,
  oldRounded: number | null | undefined,
  auto: number,
): number {
  const same = typeof oldActual === 'number' && Math.abs(oldActual - newActual) < 0.005;
  return same && typeof oldRounded === 'number' && Number.isFinite(oldRounded) ? oldRounded : auto;
}

/**
 * TRUE when a class's meta_data declares display_options.rounding_exempt
 * (EPS share counts, face value) — the rounding factor never applies, so
 * rounded = actual. Mirrors EngagementNoteDataService.IsRoundingExempt.
 */
export function isRoundingExemptMeta(metaDataJson?: string | null): boolean {
  if (!metaDataJson || metaDataJson === '{}') return false;
  try {
    const r = JSON.parse(metaDataJson) as { display_options?: { rounding_exempt?: boolean } };
    return r.display_options?.rounding_exempt === true;
  } catch { return false; }
}

/**
 * Fresh `*_rounded_auto` for a class and its line items under the CURRENT
 * factor (exempt classes: = actual). The auto value is a reference — the
 * out-of-tolerance mark and Reset rounding's target — never the displayed /
 * printed `*_rounded`, which this leaves untouched. The stored auto only
 * refreshed on Save, so after a factor change (ACTUALS → THOUSANDS) line
 * items kept the old auto and Reset restored unrounded figures until a save.
 * `pl_flow` lines keep theirs: their rounded comes from the saved Statement.
 */
export function withFreshAutoRounded<
  L extends { allocation_mode?: string; amount_actual: number; prior_amount_actual: number;
              amount_rounded_auto: number; prior_amount_rounded_auto: number },
  C extends { amount_actual: number; prior_amount_actual: number;
              amount_rounded_auto: number; prior_amount_rounded_auto: number; line_items?: L[] },
>(cd: C, factor: RoundingFactor | null | undefined, exempt: boolean): C {
  const auto = (v: number) => (exempt ? v : autoRound(v ?? 0, factor));
  return {
    ...cd,
    amount_rounded_auto: auto(cd.amount_actual),
    prior_amount_rounded_auto: auto(cd.prior_amount_actual),
    ...(cd.line_items
      ? {
          line_items: cd.line_items.map(li => (li.allocation_mode === 'pl_flow'
            ? li
            : { ...li, amount_rounded_auto: auto(li.amount_actual), prior_amount_rounded_auto: auto(li.prior_amount_actual) })),
        }
      : {}),
  };
}

/**
 * Rounded CY / PY for a `pl_flow` line (P&L profit → Reserves): the source
 * Statement's SAVED rounded value, sign-bridged like the actual (credit-
 * balance target → × −1). Falls back to auto-rounding the actual only when
 * the Statement carries no rounded value (legacy). One rounded profit for
 * P&L, Reserves and EPS — the auditor adjusts it on the P&L.
 */
export function plFlowRounded(
  flow: { amountCy?: number | null; amountPy?: number | null; amountRoundedCy?: number | null; amountRoundedPy?: number | null },
  sign: 1 | -1,
  factor: RoundingFactor | null | undefined,
): { cy: number; py: number } {
  const pick = (rounded: number | null | undefined, actual: number | null | undefined) =>
    typeof rounded === 'number' ? rounded * sign : autoRound((actual ?? 0) * sign, factor);
  return { cy: pick(flow.amountRoundedCy, flow.amountCy), py: pick(flow.amountRoundedPy, flow.amountPy) };
}

/** Round to 2 decimals (removes float noise such as 1234.5600000000001). */
export function round2(n: number): number {
  return Math.round(n * 100) / 100;
}

/**
 * The ROUNDED operand of every amount column of a row: its saved `__rounded`
 * value (which may be the auditor's adjustment), else its actual auto-rounded.
 * A computed column's rounded value is its formula over THESE — so a total
 * adds up across its rounded cells and follows a hand-adjusted rounded cell,
 * instead of being the actual total rounded on its own.
 */
export function roundedOperandMap(
  values: Record<string, number | undefined | null>,
  columns: Array<{ code: string; data_type?: string | null }>,
  factor: RoundingFactor | null | undefined,
  roundedSuffix = '__rounded',
): Record<string, number> {
  const out: Record<string, number> = {};
  for (const col of columns) {
    if (col.data_type !== 'amount') continue;
    const r = values[col.code + roundedSuffix];
    const a = values[col.code];
    if (typeof r === 'number' && Number.isFinite(r)) out[col.code] = r;
    else if (typeof a === 'number' && Number.isFinite(a)) out[col.code] = autoRound(a, factor);
  }
  return out;
}

/** Tolerance preserver — same shape the backend uses on save. Returns the
 *  user's prior rounded override when it's within tolerance of the freshly
 *  auto-rounded value; otherwise snaps to the auto. */
export function applyRoundingTolerance(
  storedRounded: number,
  newAuto: number,
  tolerance: number = DEFAULT_ROUNDING_TOLERANCE,
): number {
  if (storedRounded === 0 && newAuto !== 0) return newAuto;
  if (Math.abs(newAuto - storedRounded) <= tolerance) return storedRounded;
  return newAuto;
}

/** JSON key suffix for the rounded companion to an amount cell. The
 *  canonical actual value lives at row[col_code]; the rounded value lives
 *  at row[col_code + ROUNDED_SUFFIX]. Computed cells write both. */
export const ROUNDED_SUFFIX = '__rounded';

/** Format a rounding factor key into a human-readable units phrase
 *  (e.g. "Rupees Lakhs"). Used in section-header hints. */
export function factorUnitsLabel(factor: RoundingFactor | null | undefined): string {
  switch (factor) {
    case 'THOUSANDS': return 'Rupees Thousands';
    case 'LAKHS':     return 'Rupees Lakhs';
    case 'CRORES':    return 'Rupees Crores';
    case 'MILLIONS':  return 'Rupees Millions';
    case 'BILLIONS':  return 'Rupees Billions';
    default:          return 'Rupees';
  }
}
